<template>
  <v-container fluid class="student-dash">
    <page-skeleton v-if="loading" variant="dashboard" class="mt-4" />

    <template v-else>
      <!-- Card 86akqxu0h: formulário pendente em destaque no início. -->
      <pending-surveys-card profile="customer" />

      <!-- Card 17trt6jd8uu (retorno de 26/10): campanha de coins em destaque. -->
      <active-campaigns-card profile="customer" />

      <!-- Card 86akj8q08 (retorno de 27/10): plano de estudos vigente do
           aluno autônomo, com etapas pendentes. -->
      <study-plan-reminder-card />

      <!-- ============ HERÓI: onde você está + próxima aula ============ -->
      <div class="sd-hero">
        <div class="sd-hero__intro">
          <div class="sd-eyebrow">{{ greeting }}, {{ customerName }}</div>
          <div class="sd-hero__standing">
            {{ $t("customerDash.youAreIn") }}
            <span class="sd-display sd-hero__level">{{
              currentLevelName
            }}</span>
          </div>
          <div class="sd-hero__sub">{{ heroSubtitle }}</div>
        </div>

        <div class="sd-next" :class="{ 'sd-next--empty': !nextLesson }">
          <div class="sd-next__tag">{{ $t("customerDash.nextLesson") }}</div>

          <template v-if="nextLesson">
            <div class="sd-next__when">
              <span class="sd-next__weekday">{{
                formatWeekday(nextLesson.start)
              }}</span>
              <span class="sd-next__hour">{{
                formatHour(nextLesson.start)
              }}</span>
            </div>
            <div class="sd-next__title">{{ nextLesson.name }}</div>
            <div class="sd-next__actions">
              <v-btn
                v-if="nextLesson.link"
                depressed
                rounded
                class="sd-next__cta"
                @click="openLessonLink(nextLesson)"
              >
                <v-icon left small>mdi-video-outline</v-icon>
                {{ $t("customerDash.join") }}
              </v-btn>
              <v-btn
                text
                rounded
                small
                class="sd-next__ghost"
                :disabled="
                  !nextLesson.material || !nextLesson.material.moduleId
                "
                @click="openLessonMaterial(nextLesson)"
              >
                <v-icon left small>mdi-book-open-variant</v-icon>
                {{ $t("customerDash.material") }}
              </v-btn>
            </div>
          </template>

          <div v-else class="sd-next__none">
            {{ $t("customerDash.noNext") }}
          </div>
        </div>
      </div>

      <!-- ============ PROGRESSO + NOTIFICAÇÕES ============ -->
      <!-- Card 86aknf6a0: os boxes ficam em linhas com a mesma altura, em vez
           de uma coluna lateral empilhando quatro cards ao lado de um só. -->
      <v-row dense class="mt-4">
        <v-col cols="12" lg="7" class="sd-col">
          <div class="sd-card sd-card--pad sd-fill">
            <div class="sd-progress-head">
              <div>
                <div class="sd-section-title">
                  {{ $t("customerDash.myProgress") }}
                </div>
                <div class="sd-section-sub">
                  {{
                    dashboard.module && dashboard.module.title
                      ? $t("customerDash.currentModule", {
                          title: dashboard.module.title,
                        })
                      : $t("customerDash.pathSoon")
                  }}
                </div>
                <!-- Retorno de 22/10 (card 17trt6jcyfb): exercícios e games
                     concluídos, cada um com o seu número. -->
                <div class="sd-activity">
                  <span class="sd-activity__item">
                    <v-icon size="16" class="mr-1">mdi-pencil-outline</v-icon>
                    {{
                      $tc(
                        "customerDash.exercisesDone",
                        activity.exercisesFinished,
                        { count: activity.exercisesFinished }
                      )
                    }}
                  </span>
                  <span class="sd-activity__item">
                    <v-icon size="16" class="mr-1"
                      >mdi-gamepad-variant-outline</v-icon
                    >
                    {{
                      $tc("customerDash.gamesDone", activity.gamesCompleted, {
                        count: activity.gamesCompleted,
                      })
                    }}
                  </span>
                </div>
              </div>

              <div class="sd-bignum">
                <span class="sd-display sd-bignum__value">{{
                  formatPercentage(overallProgress.completedPercentage)
                }}</span>
                <span class="sd-bignum__meta">{{
                  $t("customerDash.lessonsCount", {
                    done: overallProgress.completedCount,
                    total: overallProgress.totalLessons,
                  })
                }}</span>
              </div>
            </div>

            <!-- Card 86ahvtd9t: o caminho inteiro, não só o que falta. Cada
                 barra separa as aulas concluídas, as de nível anterior que
                 ficaram para trás (avançadas) e as de revisão abaixo do nível
                 de entrada (extras). -->
            <div v-if="hasOverallProgress" class="sd-legend">
              <span
                v-for="item in progressLegend"
                :key="item.key"
                class="sd-legend__item"
              >
                <span
                  class="sd-legend__swatch"
                  :class="`sd-seg--${item.key}`"
                ></span>
                {{ item.label }}
                <strong>{{ item.count }}</strong>
              </span>
            </div>

            <!-- Retorno de 18/10 (card 86ahvtd9t): a trilha desce por coluna
                 (Beginner 1, Beginner 2, Essential 1...), não lado a lado. -->
            <div
              v-if="hasOverallProgress"
              class="sd-levels"
              :style="{
                '--level-rows': Math.ceil(overallProgress.levels.length / 2),
              }"
            >
              <div
                v-for="level in overallProgress.levels"
                :key="`lv-${level.level}`"
                class="sd-levels__item"
                :class="{ 'sd-levels__item--current': isCurrentLevel(level) }"
              >
                <div class="sd-levels__head">
                  <span class="sd-levels__name">
                    {{ level.title }}
                    <span
                      v-if="isCurrentLevel(level)"
                      class="sd-levels__badge"
                      >{{ $t("customerDash.currentLevel") }}</span
                    >
                  </span>
                  <span class="sd-levels__meta">{{
                    formatPercentage(level.completedPercentage)
                  }}</span>
                </div>
                <div
                  class="sd-bar sd-bar--stacked"
                  :title="levelBreakdownTitle(level)"
                >
                  <div
                    v-for="segment in levelSegments(level)"
                    :key="segment.key"
                    class="sd-bar__seg"
                    :class="`sd-seg--${segment.key}`"
                    :style="{ width: `${segment.width}%` }"
                  ></div>
                </div>
              </div>
            </div>

            <div v-else class="sd-empty">
              {{ $t("customerDash.progressEmpty") }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" lg="5" class="sd-col">
          <!-- Notificações (card 86akj8ptp): prévia só com o título, as
               fixadas primeiro. -->
          <notifications-preview
            profile="customer"
            :accent="studentAccent"
            class="sd-fill"
          />
        </v-col>
      </v-row>

      <!-- Retorno de 27/10 (card 17trt6jfe9k): selos entre o progresso e os
           três quadrados; a trilha em linha do tempo saiu. -->
      <badges-card
        profile="customer"
        class="mt-4"
        subtitle-key="pitchCoins.badgesSubStudent"
        more-to="/connect/aluno/pitch-coins"
      />

      <!-- ============ HOMEWORK + MENSALIDADE + AULAS DA SEMANA ============ -->
      <v-row dense class="mt-4">
        <v-col cols="12" md="6" lg="4" class="sd-col">
          <!-- Homework (card 86akj6ghg): o que o professor sugeriu e o aluno
                 ainda não fez, com atalho para a aula. É opcional -- sem
                 contador em destaque nem tom de cobrança. -->
          <div class="sd-card sd-card--pad sd-hw sd-fill">
            <div class="sd-section-title sd-section-title--icon">
              <v-icon size="20" class="mr-2">mdi-clipboard-text-outline</v-icon>
              {{ $t("customerDash.homework") }}
            </div>
            <div
              v-if="!homeworkLoading && suggestedHomework.length"
              class="sd-hw__hint"
            >
              {{ $t("customerDash.homeworkHint") }}
            </div>

            <page-skeleton v-if="homeworkLoading" variant="rows" :rows="2" />

            <div v-else-if="homeworkError" class="sd-note">
              {{ $t("customerDash.homeworkFailed") }}
            </div>

            <div v-else-if="suggestedHomework.length" class="sd-hw__list">
              <div
                v-for="item in suggestedHomework"
                :key="item.id"
                class="sd-hw__item"
              >
                <div class="sd-hw__body">
                  <div class="sd-hw__lesson">{{ item.lessonTitle }}</div>
                  <linkified-text
                    v-if="item.note"
                    tag="div"
                    class="sd-hw__text"
                    :text="item.note"
                  />
                  <div v-if="item.author" class="sd-hw__meta">
                    {{ item.author }}
                  </div>
                </div>
                <v-btn
                  text
                  x-small
                  class="sd-bill__link"
                  @click="openHomework(item)"
                  >{{ $t("customerDash.openLesson") }}</v-btn
                >
              </div>
            </div>

            <div v-else class="sd-note sd-note--ok">
              <v-icon size="18" color="#00A379" class="mr-2"
                >mdi-check-circle-outline</v-icon
              >
              {{ $t("customerDash.allDone") }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6" lg="4" class="sd-col">
          <!-- Mensalidade -->
          <div class="sd-card sd-card--pad sd-fill">
            <div class="sd-section-title sd-section-title--icon">
              <v-icon size="20" class="mr-2">mdi-wallet-outline</v-icon>
              {{ $t("customerDash.monthlyFee") }}
            </div>

            <div v-if="billingReminder" class="sd-bill">
              <div class="sd-bill__date">
                <span class="sd-bill__due">{{ $t("customerDash.due") }}</span>
                <span class="sd-bill__day">{{
                  formatDayMonth(billingReminder.dueDate)
                }}</span>
              </div>
              <div class="sd-bill__body">
                <div class="sd-bill__amount">
                  {{ formatCurrency(billingReminder.amount) }}
                </div>
                <div class="sd-bill__meta">
                  {{ billingReminderMeta }}
                </div>
              </div>
              <div class="sd-bill__side">
                <span
                  class="sd-pill"
                  :class="`sd-pill--${billingTone(billingReminder.status)}`"
                  >{{ billingStatusLabel(billingReminder.status) }}</span
                >
                <v-btn
                  text
                  x-small
                  class="sd-bill__link"
                  @click="$router.push('/connect/aluno/financeiro')"
                  >{{ $t("customerDash.see") }}</v-btn
                >
              </div>
            </div>

            <div v-else class="sd-note sd-note--ok">
              <v-icon size="18" color="#00A379" class="mr-2"
                >mdi-check-circle-outline</v-icon
              >
              {{ $t("customerDash.noPending") }}
            </div>
          </div>
        </v-col>

        <v-col cols="12" lg="4" class="sd-col">
          <!-- Aulas da semana -->
          <div class="sd-card sd-card--pad sd-week sd-fill">
            <div class="sd-section-title sd-section-title--icon">
              <v-icon size="20" class="mr-2">mdi-calendar-week-outline</v-icon>
              {{ $t("customerDash.weekLessons") }}
            </div>

            <div v-if="dashboard.weekLessons.length" class="sd-week__list">
              <div
                v-for="lesson in dashboard.weekLessons"
                :key="lesson.id"
                class="sd-week__item"
              >
                <div class="sd-week__date">
                  <span class="sd-week__wd">{{
                    formatWeekday(lesson.start)
                  }}</span>
                  <span class="sd-week__dm">{{
                    formatDayMonth(lesson.start)
                  }}</span>
                </div>
                <div class="sd-week__body">
                  <div class="sd-week__title">{{ lesson.name }}</div>
                  <div class="sd-week__hours">
                    {{ formatHour(lesson.start) }}–{{ formatHour(lesson.end) }}
                  </div>
                </div>
                <div class="sd-week__acts">
                  <v-btn
                    v-if="lesson.link"
                    icon
                    small
                    @click="openLessonLink(lesson)"
                  >
                    <v-icon small color="#A826E1">mdi-video-outline</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="!lesson.material || !lesson.material.moduleId"
                    @click="openLessonMaterial(lesson)"
                  >
                    <v-icon small color="#E8276F">mdi-book-open-variant</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <div v-else class="sd-note">
              {{ $t("customerDash.noWeek") }}
            </div>
          </div>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import LinkifiedText from "@/components/course/LinkifiedText.vue";
import {
  chargeMonthLabel,
  chargeOriginParts,
} from "@/shared/chargePresentation";
import NotificationsPreview from "@/components/notifications/NotificationsPreview.vue";
import PendingSurveysCard from "@/components/surveys/PendingSurveysCard.vue";
import BadgesCard from "@/components/coins/BadgesCard.vue";
import ActiveCampaignsCard from "@/components/coins/ActiveCampaignsCard.vue";
import { classThemes } from "@/shared/classThemes";
import StudyPlanReminderCard from "@/components/studyPlan/StudyPlanReminderCard.vue";
import { formatBRL, languageTag } from "@/plugins/i18n";

export default {
  name: "CustomerDashboard",

  components: {
    LinkifiedText,
    NotificationsPreview,
    PendingSurveysCard,
    BadgesCard,
    ActiveCampaignsCard,
    StudyPlanReminderCard,
  },

  async created() {
    await this.loadData();
  },

  data() {
    return {
      loading: true,
      suggestedHomework: [],
      homeworkLoading: false,
      homeworkError: false,
      dashboard: {
        module: null,
        progress: {
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          completedClasses: [],
          remainingClasses: [],
        },
        overallProgress: {
          fromLevel: null,
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          levels: [],
        },
        weekLessons: [],
        billingReminder: null,
      },
    };
  },

  computed: {
    // Rosa dos alunos (o roxo é a cor do professor).
    studentAccent() {
      return classThemes.customer.primary;
    },

    customerName() {
      return (
        this.$store.getters.getName?.split(" ")[0] ||
        this.$t("customerDash.studentFallback")
      );
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return this.$t("common.goodMorning");
      if (h < 18) return this.$t("common.goodAfternoon");
      return this.$t("common.goodEvening");
    },
    overallProgress() {
      return (
        this.dashboard.overallProgress || {
          fromLevel: null,
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          levels: [],
        }
      );
    },
    hasOverallProgress() {
      return Number(this.overallProgress.totalLessons || 0) > 0;
    },
    progressLegend() {
      const p = this.overallProgress;
      return [
        {
          key: "concluded",
          label: this.$t("customerDash.concluded"),
          count: +p.concludedCount || 0,
        },
        {
          key: "advanced",
          label: this.$t("customerDash.advanced"),
          count: +p.advancedCount || 0,
        },
        {
          key: "extra",
          label: this.$t("customerDash.extra"),
          count: +p.extraCount || 0,
        },
        {
          key: "pending",
          label: this.$t("customerDash.pending"),
          count: +p.pendingCount || 0,
        },
      ];
    },
    activity() {
      return (
        this.dashboard.activity || { exercisesFinished: 0, gamesCompleted: 0 }
      );
    },
    billingReminder() {
      return this.dashboard.billingReminder || null;
    },
    // Card 86ak0nkuq: o mes de referencia identifica a cobranca; professor e
    // turma so para quem tem turma -- a do aluno solo e da plataforma.
    billingReminderMeta() {
      const charge = this.billingReminder;
      if (!charge) return "";
      return [
        chargeMonthLabel(charge.month, charge.year),
        ...chargeOriginParts(charge),
      ]
        .filter(Boolean)
        .join(" · ");
    },
    nextLesson() {
      return this.dashboard.weekLessons?.[0] || null;
    },
    // A Escada: níveis ordenados, com estado done/current/todo.
    ladder() {
      const levels = [...(this.overallProgress.levels || [])].sort(
        (a, b) => Number(a.level) - Number(b.level)
      );
      if (!levels.length) return [];

      // Nível atual: o do módulo, senão o primeiro incompleto, senão o último.
      const moduleTitle = this.dashboard.module?.title;
      let currentIdx = levels.findIndex((l) => l.title === moduleTitle);
      if (currentIdx === -1) {
        currentIdx = levels.findIndex(
          (l) => Number(l.completedPercentage || 0) < 100
        );
      }
      if (currentIdx === -1) currentIdx = levels.length - 1;

      return levels.map((l, i) => {
        let state = "todo";
        if (i < currentIdx) state = "done";
        else if (i === currentIdx) state = "current";
        return {
          ...l,
          state,
          // trilho preenchido = tudo até o nó atual (inclusive)
          filledRail: i <= currentIdx,
        };
      });
    },
    currentLevelName() {
      const cur = this.ladder.find((s) => s.state === "current");
      return (
        cur?.title ||
        this.dashboard.module?.title ||
        this.$t("customerDash.yourPlacement")
      );
    },
    doneCount() {
      return this.ladder.filter((s) => s.state === "done").length;
    },
    ladderAria() {
      if (!this.ladder.length) return "";
      return this.$t("customerDash.ladderAria", {
        level: this.currentLevelName,
        done: this.doneCount,
        total: this.ladder.length,
      });
    },
    heroSubtitle() {
      if (this.dashboard.module?.title) {
        return this.$t("customerDash.heroModule", {
          title: this.dashboard.module.title,
        });
      }
      return this.$t("customerDash.heroEmpty");
    },
  },

  methods: {
    async loadData() {
      this.loading = true;
      try {
        const res = await pitchCustomerApi.getDashboard();
        this.dashboard = {
          module: res.data?.module || null,
          progress: res.data?.progress || this.dashboard.progress,
          overallProgress:
            res.data?.overallProgress || this.dashboard.overallProgress,
          weekLessons: Array.isArray(res.data?.weekLessons)
            ? res.data.weekLessons
            : [],
          billingReminder: res.data?.billingReminder || null,
          activity: res.data?.activity || null,
        };
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: this.$t("customerDash.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
      this.loadHomework();
    },
    // Card 86akj6ghg: homeworks ainda não feitos. Chamada à parte, para uma falha
    // aqui não derrubar o painel -- o cartão só mostra o aviso de erro.
    async loadHomework() {
      this.homeworkLoading = true;
      this.homeworkError = false;
      try {
        const res = await pitchCustomerApi.getPendingHomework();
        this.suggestedHomework = Array.isArray(res.data?.homework)
          ? res.data.homework
          : [];
      } catch (error) {
        this.suggestedHomework = [];
        this.homeworkError = true;
      } finally {
        this.homeworkLoading = false;
      }
    },
    openHomework(item) {
      if (!item?.contentId) return;
      this.$router.push(`/connect/aluno/curso/${item.contentId}`);
    },
    openLessonMaterial(lesson) {
      if (!lesson?.material?.moduleId) return;
      this.openCourseContent(lesson.material, lesson.link);
    },
    openCourseContent(material, classLink = null) {
      if (!material?.moduleId) return;
      const query = {};
      if (material.materialId) query.material = material.materialId;
      if (classLink) query.classLink = classLink;
      this.$router.push({
        path: `/connect/aluno/curso/${material.moduleId}`,
        query,
      });
    },
    // Registra a entrada (presença sugerida) sem atrasar nem barrar o Meet.
    openLessonLink(lesson) {
      if (!lesson?.link) return;
      if (lesson.id) {
        Promise.resolve(pitchCustomerApi.joinLesson(lesson.id)).catch(() => {});
      }
      window.open(lesson.link, "_blank");
    },
    parseDate(value) {
      if (!value) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        const [y, m, d] = String(value).split("-").map(Number);
        return new Date(y, m - 1, d, 12, 0, 0, 0);
      }
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    formatWeekday(value) {
      const date = this.parseDate(value);
      if (!date) return "-";
      return new Intl.DateTimeFormat(languageTag(), { weekday: "short" })
        .format(date)
        .replace(".", "")
        .toUpperCase();
    },
    formatDayMonth(value) {
      const date = this.parseDate(value);
      if (!date) return "-";
      return new Intl.DateTimeFormat(languageTag(), {
        day: "2-digit",
        month: "2-digit",
      }).format(date);
    },
    formatHour(value) {
      const date = this.parseDate(value);
      if (!date) return "--:--";
      return new Intl.DateTimeFormat(languageTag(), {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(date);
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(0)}%`;
    },
    clampPct(value) {
      return Math.max(0, Math.min(100, Number(value || 0)));
    },
    isCurrentLevel(level) {
      return level.status === "current";
    },
    // Ordem das cores na barra: revisão abaixo do nível de entrada, aulas
    // concluídas, aulas avançadas e, por último, o que ainda falta (trilho).
    levelSegments(level) {
      const total = Number(level.totalLessons || 0);
      if (!total) return [];

      return ["extra", "concluded", "advanced"]
        .map((key) => ({
          key,
          width: this.clampPct(
            (Number(level[`${key}Count`] || 0) / total) * 100
          ),
        }))
        .filter((segment) => segment.width > 0);
    },
    levelBreakdownTitle(level) {
      const parts = [
        [level.concludedCount, "customerDash.bdConcluded"],
        [level.advancedCount, "customerDash.bdAdvanced"],
        [level.extraCount, "customerDash.bdExtra"],
        [level.pendingCount, "customerDash.bdPending"],
      ]
        .filter(([count]) => Number(count) > 0)
        .map(([count, key]) => this.$t(key, { count }));

      return (
        parts.join(" · ") ||
        this.$t("customerDash.bdTotal", { count: level.totalLessons })
      );
    },
    formatCurrency(value) {
      return formatBRL(value);
    },
    billingStatusLabel(status) {
      return status === "OVERDUE"
        ? this.$t("customerFinance.overdue")
        : this.$t("customerFinance.pending");
    },
    billingTone(status) {
      return status === "OVERDUE" ? "danger" : "warn";
    },
  },
};
</script>

<style scoped>
.student-dash {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  --pink-soft: rgba(232, 39, 111, 0.1);
  --grape: var(--dk-grape, #a826e1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  width: 100%;
  padding: 8px 4px 40px;
  color: var(--ink);
}

.sd-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.sd-eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pink);
}

.sd-section-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.sd-section-title--icon {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}
.sd-section-title--icon .v-icon {
  color: var(--pink);
}
.sd-section-sub {
  margin-top: 3px;
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.4;
}

/* Retorno de 22/10: exercícios e games concluídos */
.sd-activity {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 8px;
  font-size: 0.82rem;
  color: var(--muted);
}

.sd-activity__item {
  display: inline-flex;
  align-items: center;
}

.sd-activity__item .v-icon {
  color: var(--dk-pink, #e8276f);
}

/* ---------- Card base ---------- */
.sd-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.sd-card--pad {
  padding: 22px;
}

/* ---------- Herói ---------- */
.sd-hero {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: stretch;
  margin-top: 12px;
  padding: 26px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 140% at 100% 0%,
      rgba(168, 38, 225, 0.08),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}
.sd-hero__standing {
  margin-top: 10px;
  font-size: 1.15rem;
  font-weight: 500;
  color: var(--muted);
  line-height: 1.35;
}
.sd-hero__level {
  display: block;
  font-size: clamp(2rem, 4.4vw, 2.9rem);
  font-weight: 700;
  color: var(--ink);
  line-height: 1.02;
  margin-top: 2px;
}
.sd-hero__sub {
  margin-top: 12px;
  font-size: 0.9rem;
  color: var(--muted);
  max-width: 42ch;
}

/* Próxima aula (CTA) */
.sd-next {
  min-width: 224px;
  padding: 18px 20px;
  border-radius: 18px;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #e8276f) 0%,
    var(--dk-pink-deep, #b51f57) 100%
  );
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 18px 34px -20px rgba(232, 39, 111, 0.7);
}
.sd-next--empty {
  background: var(--dk-paper, #f5f1f6);
  color: var(--muted);
  box-shadow: none;
  border: 1px dashed var(--line);
}
.sd-next__tag {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.85;
}
.sd-next__when {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 8px;
}
.sd-next__weekday {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.sd-next__hour {
  font-size: 1.5rem;
  font-weight: 800;
}
.sd-next__title {
  margin-top: 2px;
  font-size: 0.88rem;
  font-weight: 600;
  opacity: 0.92;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-next__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
}
.sd-next__cta.v-btn {
  background: var(--dk-surface, #fff) !important;
  color: var(--pink) !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}
.sd-next__ghost.v-btn {
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
}
.sd-next__none {
  margin-top: 10px;
  font-size: 0.85rem;
}

/* ---------- A Escada ---------- */
.sd-ladder-card {
  margin-top: 16px;
  padding: 22px 26px 18px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.sd-ladder-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 30px;
}
.sd-ladder-count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--muted);
}
.sd-ladder {
  display: flex;
  align-items: center;
}
.sd-step {
  position: relative;
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}
.sd-step__rail {
  flex: 1;
  height: 4px;
  border-radius: 4px;
  background: var(--dk-line, #ece7ef);
}
.sd-step--first .sd-step__rail {
  display: none;
}
.sd-step__rail--filled {
  background: linear-gradient(
    90deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink, #ff2b7a)
  );
}
.sd-step__node {
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  margin-left: 6px;
  border-radius: 50%;
  background: var(--dk-surface, #fff);
  border: 2px solid var(--dk-line, #dcd5e0);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}
.sd-step--done .sd-step__node {
  background: var(--pink);
  border-color: var(--pink);
}
.sd-step--current .sd-step__node {
  width: 26px;
  height: 26px;
  border-color: var(--pink);
  border-width: 3px;
  background: var(--dk-surface, #fff);
  box-shadow: 0 0 0 6px var(--pink-soft),
    0 8px 18px -6px rgba(232, 39, 111, 0.6);
}
.sd-step__flag {
  position: absolute;
  bottom: calc(100% + 12px);
  right: 13px;
  transform: translateX(50%);
  white-space: nowrap;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.sd-step__flag::after {
  content: "";
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 8px;
  background: var(--pink);
  border-radius: 2px;
}
.sd-ladder-ends {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--muted);
}

/* ---------- Progresso ---------- */
.sd-progress-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.sd-bignum {
  text-align: right;
  flex: 0 0 auto;
}
.sd-bignum__value {
  display: block;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1;
  color: var(--pink);
}
.sd-bignum__meta {
  font-size: 0.76rem;
  color: var(--muted);
}
.sd-levels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(var(--level-rows, 1), auto);
  grid-auto-flow: column;
  gap: 16px 24px;
}
.sd-levels__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
}
.sd-levels__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-levels__meta {
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--muted);
  margin-left: 8px;
  font-variant-numeric: tabular-nums;
}
.sd-bar {
  height: 7px;
  border-radius: 6px;
  background: var(--dk-line, #efeaf1);
  overflow: hidden;
}
.sd-bar__fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink, #ff2b7a)
  );
  transition: width 0.5s ease;
}
.sd-bar__fill--done {
  background: var(--teal);
}
.sd-bar--stacked {
  display: flex;
}
.sd-bar__seg {
  height: 100%;
  transition: width 0.5s ease;
}
.sd-seg--concluded {
  background: linear-gradient(
    90deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink, #ff2b7a)
  );
}
.sd-seg--advanced {
  background: repeating-linear-gradient(
    135deg,
    var(--dk-pink-wash, #f6b3cc) 0 4px,
    var(--dk-pink-wash, #fbd5e3) 4px 8px
  );
}
.sd-seg--extra {
  background: #7c4dcc;
}
.sd-seg--pending {
  background: var(--dk-line, #efeaf1);
}
.sd-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 10px 0 4px;
  font-size: 0.8rem;
  color: var(--dk-muted, #6b6472);
}
.sd-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sd-legend__item strong {
  color: var(--dk-ink, #231a2e);
}
.sd-legend__swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
}
.sd-levels__badge {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: var(--dk-pink-wash, #fde7f0);
  color: var(--dk-pink, #e8276f);
}

/* ---------- Grade de boxes (card 86aknf6a0) ---------- */
/* Cada coluna estica o card até a altura da linha, então os boxes lado a
   lado terminam na mesma linha. */
.sd-col {
  display: flex;
  flex-direction: column;
}
.sd-fill {
  flex: 1 1 auto;
}
.sd-empty,
.sd-note {
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.5;
}
.sd-note {
  display: flex;
  align-items: center;
}
.sd-note--ok {
  color: var(--ink);
}

/* Mensalidade */
.sd-bill {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.sd-bill__date {
  text-align: center;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(255, 93, 42, 0.1);
  color: var(--ember);
  min-width: 56px;
}
.sd-bill__due {
  display: block;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}
.sd-bill__day {
  display: block;
  margin-top: 2px;
  font-size: 0.92rem;
  font-weight: 800;
}
.sd-bill__amount {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--ink);
}
.sd-bill__meta {
  margin-top: 2px;
  font-size: 0.78rem;
  color: var(--muted);
}
.sd-bill__side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.sd-bill__link.v-btn {
  color: var(--pink) !important;
  text-transform: none;
  letter-spacing: 0;
}
.sd-pill {
  font-size: 0.66rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sd-pill--warn {
  background: rgba(255, 93, 42, 0.14);
  color: var(--dk-ember-deep, #b5421e);
}
.sd-pill--danger {
  background: rgba(213, 35, 35, 0.14);
  color: var(--dk-red, #a61b1b);
}

/* Aulas da semana */
.sd-week__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 268px;
  overflow: auto;
}
.sd-week__item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.sd-week__date {
  text-align: center;
  min-width: 48px;
}
.sd-week__wd {
  display: block;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--pink);
}
.sd-week__dm {
  display: block;
  margin-top: 1px;
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--ink);
}
.sd-week__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-week__hours {
  font-size: 0.76rem;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.sd-week__acts {
  display: flex;
  align-items: center;
}

/* Homework (opcional: sem badge nem cor de alerta) */
.sd-hw__hint {
  margin: -6px 0 10px;
  font-size: 0.8rem;
  color: var(--muted);
}
.sd-hw__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 268px;
  overflow: auto;
}
.sd-hw__item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.sd-hw__body {
  min-width: 0;
}
.sd-hw__lesson {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sd-hw__text {
  margin-top: 2px;
  font-size: 0.8rem;
  color: var(--ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-line;
}
.sd-hw__meta {
  margin-top: 2px;
  font-size: 0.74rem;
  color: var(--muted);
}

/* ---------- Responsivo ---------- */
@media (max-width: 860px) {
  .sd-hero {
    grid-template-columns: 1fr;
  }
  .sd-next {
    min-width: 0;
  }
}
@media (max-width: 600px) {
  .sd-levels {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-flow: row;
  }
  .sd-step__node {
    width: 12px;
    height: 12px;
    margin-left: 4px;
  }
  .sd-step--current .sd-step__node {
    width: 22px;
    height: 22px;
  }
  .sd-step__flag {
    font-size: 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sd-bar__fill,
  .sd-step__node {
    transition: none;
  }
}
</style>
